import type { Ref } from 'vue'
import type { ApiToken } from '../interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'

import { useApiTokensStore } from '../store'
import { useApiTokenUpdateApi } from '../../../api/api-tokens/update.hook'
import { useApiTokensApi } from '../../../api/api-tokens/list.hook'

interface ModalEditApiTokenReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refToken: ComputedRef<ApiToken | null>
  refTitle: ComputedRef<string>
  refName: Ref<string>
  refExpiresAt: Ref<Date | null>
  refMinExpiresDate: Date
  refIsValid: ComputedRef<boolean>
  getUserHref: (token: ApiToken) => string
  formatDateTime: (value: string | null) => string
  setName: (value: string | number | null) => void
  closeModal: () => void
  saveToken: () => Promise<void>
}

export const useModalEditApiToken = (): ModalEditApiTokenReturn => {
  const store = useApiTokensStore()
  const dayjs = useDayjs()

  const refIsLoading = ref(false)
  const refName = ref('')
  const refExpiresAt = ref<Date | null>(null)
  const refMinExpiresDate = dayjs().toDate()

  const refIsOpen = computed<boolean>(() => store.isModalEdit)
  const refToken = computed<ApiToken | null>(() => store.tokenToEdit)
  const refTitle = computed<string>(() => `Edit API token #${refToken.value?.id ?? ''}`)
  const refIsValid = computed<boolean>(() => refName.value.trim().length > 0)

  const getUserHref = (token: ApiToken): string =>
    token.user ? (useAdminRoutes('admin.user.show', { user_id: token.user.id }) ?? '') : ''

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const setName = (value: string | number | null): void => {
    refName.value = String(value ?? '')
  }

  const closeModal = (): void => {
    store.setTokenToEdit(null)
    refName.value = ''
    refExpiresAt.value = null
  }

  const saveToken = async (): Promise<void> => {
    const token = refToken.value
    if (!token || !refIsValid.value || refIsLoading.value) return

    await useApiTokenUpdateApi({
      tokenId: token.id,
      name: refName.value.trim(),
      expiresAt: refExpiresAt.value ? dayjs(refExpiresAt.value).format(DateFormat.DefaultDate) : null,
      onStart: () => { refIsLoading.value = true },
      onSuccess: () => {
        closeModal()
        void useApiTokensApi()
      },
      onFinish: () => { refIsLoading.value = false },
    })
  }

  watch(refToken, (token) => {
    if (!token) return

    refName.value = token.name

    // An already-expired date can't be re-saved (min date is today), so a
    // past expiry is lifted to today — editing an expired token extends it
    // by a day rather than turning it into a never-expiring one.
    if (!token.expires_at) {
      refExpiresAt.value = null
      return
    }

    const expiry = dayjs(token.expires_at)
    refExpiresAt.value = expiry.isBefore(dayjs(), 'day')
      ? dayjs().toDate()
      : expiry.toDate()
  })

  return {
    refIsOpen,
    refIsLoading,
    refToken,
    refTitle,
    refName,
    refExpiresAt,
    refMinExpiresDate,
    refIsValid,
    getUserHref,
    formatDateTime,
    setName,
    closeModal,
    saveToken,
  }
}
